import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";

const GST_RATE = 0.10;

function money(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("en-AU", { style: "currency", currency: "AUD" });
}

/**
 * QuoteBuilder — Phase 1 MVP
 * - `?lead=<id>` → create-mode; POST /painter/quotes and switch to edit-mode
 * - `/painter/quotes/:id` → edit-mode; PATCH on save
 */
export default function QuoteBuilder() {
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(false);
  const [jobsAccess, setJobsAccess] = useState(false);
  const [converting, setConverting] = useState(false);

  // Local editable state
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState("");
  const [homeowner, setHomeowner] = useState({ name: "", email: "", phone: "", address: "" });

  // Job Workflow feature-flag probe — surfaces the Convert-to-Job button
  // only when the caller has access (Owner or JOB_WORKFLOW_ENABLED=true).
  useEffect(() => {
    let cancelled = false;
    api.get("/pro/jobs/access")
      .then((r) => { if (!cancelled) setJobsAccess(Boolean(r.data?.allowed)); })
      .catch(() => { if (!cancelled) setJobsAccess(false); });
    return () => { cancelled = true; };
  }, []);

  async function convertToJob() {
    if (!quote || quote.status !== "accepted" || converting) return;
    setConverting(true);
    setErr("");
    try {
      const { data } = await api.post("/pro/jobs/convert-from-quote", { quote_id: quote.id });
      nav(`/pro/jobs/${data.job.id}`);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setConverting(false);
    }
  }

  // Bootstrap: create-mode or edit-mode
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      setErr("");
      setLoading(true);
      try {
        let doc;
        if (routeId) {
          doc = (await api.get(`/painter/quotes/${routeId}`)).data;
        } else {
          const leadId = searchParams.get("lead");
          if (!leadId) throw new Error("Missing lead id");
          doc = (await api.post("/painter/quotes", { lead_id: leadId })).data;
          // Replace URL with canonical edit path
          nav(`/painter/quotes/${doc.id}`, { replace: true });
        }
        if (cancelled) return;
        setQuote(doc);
        setItems(doc.line_items || []);
        setNotes(doc.notes || "");
        setTerms(doc.terms || "");
        setHomeowner(doc.homeowner || { name: "", email: "", phone: "", address: "" });
      } catch (e) {
        if (!cancelled) setErr(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    bootstrap();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // Local total calculation (server is the source of truth on save)
  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
    const gst = subtotal * GST_RATE;
    return { subtotal, gst, total: subtotal + gst };
  }, [items]);

  function updateItem(idx, patch) {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  }

  function addItem() {
    setItems((prev) => [...prev, { id: crypto.randomUUID(), description: "", quantity: 1, unit_price: 0, line_total: 0 }]);
  }

  function removeItem(idx) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  async function save() {
    if (saving) return;
    setSaving(true);
    setErr("");
    try {
      const payload = {
        line_items: items.map((i) => ({
          id: i.id,
          description: i.description,
          quantity: Number(i.quantity) || 0,
          unit_price: Number(i.unit_price) || 0,
        })),
        notes, terms,
        homeowner_name: homeowner.name,
        homeowner_email: homeowner.email,
        homeowner_phone: homeowner.phone,
        homeowner_address: homeowner.address,
      };
      const { data } = await api.patch(`/painter/quotes/${quote.id}`, payload);
      setQuote(data);
      setItems(data.line_items || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="p-8 text-brand-navy" data-testid="quote-builder-loading">Loading quote…</div>;
  }
  if (err && !quote) {
    return (
      <div className="p-8">
        <div className="text-red-700 mb-4" data-testid="quote-builder-error">{err}</div>
        <Link className="text-brand-teal underline" to="/painter/leads">Back to leads</Link>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl" data-testid="quote-builder-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold">Quote builder · Draft</div>
          <h1 className="text-2xl font-bold text-brand-navy">{quote.quote_number}</h1>
          <p className="text-sm text-gray-600">For {homeowner.name || "—"} · created {new Date(quote.created_at).toLocaleDateString("en-AU")}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPreview((p) => !p)}
            className="px-4 py-2 rounded-lg bg-white border border-brand-navy text-brand-navy font-semibold hover:bg-brand-cream text-sm"
            data-testid="quote-preview-toggle"
          >
            {preview ? "Edit" : "Preview"}
          </button>
          <button
            onClick={save}
            disabled={saving || preview || quote.status !== "draft"}
            className="px-4 py-2 rounded-lg bg-brand-navy text-white font-semibold hover:bg-brand-navy/90 disabled:opacity-50 text-sm"
            data-testid="quote-save-draft"
          >
            {saving ? "Saving…" : "Save draft"}
          </button>
          <button
            onClick={async () => {
              if (sending) return;
              if (!homeowner.email || !homeowner.email.includes("@")) {
                setErr("Homeowner email required before sending.");
                return;
              }
              if (!window.confirm(`Send this quote to ${homeowner.email}?`)) return;
              setSending(true);
              setErr("");
              try {
                const { data } = await api.post(`/painter/quotes/${quote.id}/send`);
                setQuote(data);
              } catch (e) {
                setErr(formatApiError(e));
              } finally {
                setSending(false);
              }
            }}
            disabled={sending || quote.status === "accepted"}
            className="px-4 py-2 rounded-lg bg-brand-teal text-white font-semibold hover:bg-brand-teal/90 disabled:opacity-50 text-sm"
            data-testid="quote-send"
          >
            {sending ? "Sending…" : quote.status === "sent" ? "Re-send" : quote.status === "accepted" ? "Accepted" : "Send quote"}
          </button>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/painter/quotes/${quote.id}/pdf`}
            target="_blank" rel="noreferrer"
            className="px-4 py-2 rounded-lg bg-white border border-brand-navy text-brand-navy font-semibold hover:bg-brand-cream text-sm"
            data-testid="quote-download-pdf"
          >
            PDF
          </a>
          {jobsAccess && quote.status === "accepted" && (
            <button
              onClick={convertToJob}
              disabled={converting}
              data-testid="convert-quote-to-job-btn"
              className="px-4 py-2 rounded-lg bg-brand-teal text-white font-semibold hover:bg-brand-teal/90 disabled:opacity-50 text-sm"
            >
              {converting ? "Converting…" : "Convert to Job"}
            </button>
          )}
        </div>
      </div>

      {err && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="quote-inline-error">{err}</div>}

      {preview ? (
        <QuotePreviewPane quote={{ ...quote, line_items: items, notes, terms, homeowner, ...totals }} />
      ) : (
        <QuoteEditPane
          quote={quote}
          items={items}
          notes={notes}
          terms={terms}
          homeowner={homeowner}
          totals={totals}
          updateItem={updateItem}
          addItem={addItem}
          removeItem={removeItem}
          setNotes={setNotes}
          setTerms={setTerms}
          setHomeowner={setHomeowner}
        />
      )}
    </div>
  );
}

function QuoteEditPane({ quote, items, notes, terms, homeowner, totals, updateItem, addItem, removeItem, setNotes, setTerms, setHomeowner }) {
  return (
    <>
      <section className="mb-6 grid md:grid-cols-2 gap-4" data-testid="quote-edit-parties">
        <div className="border border-brand-border rounded-lg p-4 bg-white">
          <h3 className="text-xs font-bold tracking-widest text-brand-teal uppercase mb-3">From (Painter)</h3>
          <div className="text-sm text-brand-navy">
            <div className="font-semibold">{quote.painter_business.business_name || "—"}</div>
            <div className="text-gray-700">ABN {quote.painter_business.abn || "—"}</div>
            <div className="text-gray-700">{quote.painter_business.contact_email}</div>
            <div className="text-gray-700">{quote.painter_business.contact_phone || ""}</div>
          </div>
        </div>
        <div className="border border-brand-border rounded-lg p-4 bg-white">
          <h3 className="text-xs font-bold tracking-widest text-brand-teal uppercase mb-3">To (Homeowner)</h3>
          <div className="space-y-2">
            <input className="w-full px-3 py-2 border rounded text-sm" placeholder="Name" value={homeowner.name} onChange={(e) => setHomeowner({ ...homeowner, name: e.target.value })} data-testid="homeowner-name" />
            <input className="w-full px-3 py-2 border rounded text-sm" placeholder="Email" value={homeowner.email} onChange={(e) => setHomeowner({ ...homeowner, email: e.target.value })} data-testid="homeowner-email" />
            <input className="w-full px-3 py-2 border rounded text-sm" placeholder="Phone" value={homeowner.phone} onChange={(e) => setHomeowner({ ...homeowner, phone: e.target.value })} data-testid="homeowner-phone" />
            <input className="w-full px-3 py-2 border rounded text-sm" placeholder="Project address" value={homeowner.address} onChange={(e) => setHomeowner({ ...homeowner, address: e.target.value })} data-testid="homeowner-address" />
          </div>
        </div>
      </section>

      <section className="mb-6 bg-white border border-brand-border rounded-lg overflow-hidden" data-testid="quote-line-items">
        <div className="p-4 flex items-center justify-between border-b border-brand-border">
          <h3 className="text-xs font-bold tracking-widest text-brand-teal uppercase">Line items</h3>
          <button onClick={addItem} className="px-3 py-1.5 rounded bg-brand-teal text-white text-xs font-semibold hover:bg-brand-teal/90" data-testid="add-line-item">+ Add item</button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-brand-cream text-brand-navy">
            <tr>
              <th className="text-left px-4 py-2 w-1/2">Description</th>
              <th className="text-right px-3 py-2 w-24">Qty</th>
              <th className="text-right px-3 py-2 w-32">Unit price</th>
              <th className="text-right px-3 py-2 w-32">Line total</th>
              <th className="w-12" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="text-center text-gray-500 italic py-8">No line items yet. Click &quot;+ Add item&quot; to start.</td></tr>
            )}
            {items.map((item, idx) => {
              const line = (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
              return (
                <tr key={item.id || idx} className="border-t border-brand-border" data-testid={`quote-line-item-${idx}`}>
                  <td className="px-4 py-2"><input className="w-full px-2 py-1 text-sm border rounded" value={item.description || ""} onChange={(e) => updateItem(idx, { description: e.target.value })} placeholder="e.g. Interior walls — 3 coats" data-testid={`line-desc-${idx}`} /></td>
                  <td className="px-3 py-2"><input type="number" step="any" min="0" className="w-full px-2 py-1 text-sm border rounded text-right" value={item.quantity ?? 0} onChange={(e) => updateItem(idx, { quantity: e.target.value })} data-testid={`line-qty-${idx}`} /></td>
                  <td className="px-3 py-2"><input type="number" step="0.01" min="0" className="w-full px-2 py-1 text-sm border rounded text-right" value={item.unit_price ?? 0} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} data-testid={`line-price-${idx}`} /></td>
                  <td className="px-3 py-2 text-right font-semibold text-brand-navy" data-testid={`line-total-${idx}`}>{money(line)}</td>
                  <td className="px-2 py-2 text-center"><button onClick={() => removeItem(idx)} className="text-red-600 hover:text-red-800 text-xs" data-testid={`line-remove-${idx}`}>✕</button></td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-brand-cream/50 text-brand-navy">
            <tr><td colSpan={3} className="px-4 py-2 text-right text-sm font-semibold">Subtotal</td><td className="px-3 py-2 text-right text-sm font-semibold" data-testid="totals-subtotal">{money(totals.subtotal)}</td><td /></tr>
            <tr><td colSpan={3} className="px-4 py-2 text-right text-sm">GST (10%)</td><td className="px-3 py-2 text-right text-sm" data-testid="totals-gst">{money(totals.gst)}</td><td /></tr>
            <tr className="border-t-2 border-brand-navy"><td colSpan={3} className="px-4 py-3 text-right text-base font-bold">Total</td><td className="px-3 py-3 text-right text-base font-bold" data-testid="totals-total">{money(totals.total)}</td><td /></tr>
          </tfoot>
        </table>
      </section>

      <section className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold tracking-widest text-brand-teal uppercase mb-2">Notes</label>
          <textarea className="w-full h-32 px-3 py-2 border border-brand-border rounded text-sm" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Extra detail visible to the homeowner…" data-testid="quote-notes" />
        </div>
        <div>
          <label className="block text-xs font-bold tracking-widest text-brand-teal uppercase mb-2">Terms &amp; conditions</label>
          <textarea className="w-full h-32 px-3 py-2 border border-brand-border rounded text-sm" value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="Deposit, payment, warranty terms…" data-testid="quote-terms" />
        </div>
      </section>
    </>
  );
}

function QuotePreviewPane({ quote }) {
  return (
    <div className="bg-white shadow-lg border border-brand-border rounded-lg p-10 max-w-4xl mx-auto" data-testid="quote-preview-pane">
      <div className="flex items-start justify-between mb-8 pb-6 border-b border-brand-border">
        <div>
          <img src="/branding/primary-logo.png" alt="ColourMatch" className="h-10 mb-3" />
          <div className="text-xs text-gray-500 tracking-widest uppercase">Quote</div>
          <h2 className="text-3xl font-bold text-brand-navy">{quote.quote_number}</h2>
        </div>
        <div className="text-right text-sm text-gray-700">
          <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">Date</div>
          <div className="font-semibold">{new Date(quote.created_at).toLocaleDateString("en-AU", { day: "2-digit", month: "long", year: "numeric" })}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-8 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand-teal font-bold mb-2">From</div>
          <div className="text-brand-navy font-semibold">{quote.painter_business.business_name || "—"}</div>
          {quote.painter_business.abn && <div className="text-sm text-gray-700">ABN {quote.painter_business.abn}</div>}
          <div className="text-sm text-gray-700">{quote.painter_business.contact_email}</div>
          {quote.painter_business.contact_phone && <div className="text-sm text-gray-700">{quote.painter_business.contact_phone}</div>}
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-brand-teal font-bold mb-2">Prepared for</div>
          <div className="text-brand-navy font-semibold">{quote.homeowner.name || "—"}</div>
          <div className="text-sm text-gray-700">{quote.homeowner.email}</div>
          {quote.homeowner.phone && <div className="text-sm text-gray-700">{quote.homeowner.phone}</div>}
          {quote.homeowner.address && <div className="text-sm text-gray-700 mt-1">{quote.homeowner.address}</div>}
        </div>
      </div>

      <table className="w-full text-sm mb-6">
        <thead className="border-b-2 border-brand-navy text-brand-navy">
          <tr>
            <th className="text-left py-2">Description</th>
            <th className="text-right py-2 w-20">Qty</th>
            <th className="text-right py-2 w-28">Unit price</th>
            <th className="text-right py-2 w-28">Line total</th>
          </tr>
        </thead>
        <tbody>
          {quote.line_items.map((i) => (
            <tr key={i.id} className="border-b border-brand-border">
              <td className="py-2 pr-2">{i.description}</td>
              <td className="py-2 text-right">{i.quantity}</td>
              <td className="py-2 text-right">{money(i.unit_price)}</td>
              <td className="py-2 text-right font-semibold">{money(i.quantity * i.unit_price)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr><td colSpan={3} className="py-2 text-right">Subtotal</td><td className="py-2 text-right font-semibold">{money(quote.subtotal)}</td></tr>
          <tr><td colSpan={3} className="py-2 text-right">GST (10%)</td><td className="py-2 text-right">{money(quote.gst)}</td></tr>
          <tr className="border-t-2 border-brand-navy text-brand-navy"><td colSpan={3} className="py-3 text-right text-lg font-bold">Total (incl. GST)</td><td className="py-3 text-right text-lg font-bold">{money(quote.total)}</td></tr>
        </tfoot>
      </table>

      {quote.notes && (
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-brand-teal font-bold mb-2">Notes</div>
          <p className="text-sm text-gray-800 whitespace-pre-line">{quote.notes}</p>
        </div>
      )}

      {quote.terms && (
        <div className="mb-6 border-t border-brand-border pt-4">
          <div className="text-xs uppercase tracking-widest text-brand-teal font-bold mb-2">Terms &amp; conditions</div>
          <p className="text-sm text-gray-700 whitespace-pre-line">{quote.terms}</p>
        </div>
      )}

      <div className="text-center text-xs text-gray-500 mt-10 pt-6 border-t border-brand-border">
        Powered by ColourMatch · colourmatch.au
      </div>
    </div>
  );
}
